import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import type { ReactNode, ComponentType } from 'react'
import Modal from '../components/ui/modal' // your existing Modal (default export)

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

export type ModalOptions = {
  title?: string
  size?: ModalSize
  showHeader?: boolean
  closeOnBackdrop?: boolean
  onClose?: () => void
  className?: string
}

type ShowModal = (content: ReactNode, opts?: ModalOptions) => void
type HideModal = () => void
type SetContent = (content: ReactNode) => void

type ShowTemplate = <P extends object>(
  Comp: ComponentType<P>,
  props?: P,
  opts?: ModalOptions
) => void

export type ModalContextType = {
  isOpen: boolean
  showModal: ShowModal
  hideModal: HideModal
  setModalContent: SetContent
  showTemplate: ShowTemplate
}

const ModalContext = createContext<ModalContextType | null>(null)

export function useModal(): ModalContextType {
  const ctx = useContext(ModalContext)
  if (!ctx) throw new Error('useModal must be used within <ModalProvider>')
  return ctx
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [content, setContent] = useState<ReactNode>(null)
  const [opts, setOpts] = useState<ModalOptions>({
    size: 'lg',
    showHeader: true,
    closeOnBackdrop: true
  })

  const hideModal: HideModal = useCallback(() => {
    setIsOpen(false)
    setTimeout(() => setContent(null), 0)
    opts?.onClose?.()
  }, [opts])

  const showModal: ShowModal = useCallback((node, newOpts) => {
    setContent(node)
    setOpts(prev => ({ ...prev, ...newOpts }))
    setIsOpen(true)
  }, [])

  const setModalContent: SetContent = useCallback(node => {
    setContent(node)
  }, [])

  const showTemplate: ShowTemplate = useCallback(
    (Comp, props, newOpts) => {
      showModal(<Comp {...(props as any)} />, newOpts)
    },
    [showModal]
  )

  const value = useMemo(
    () => ({
      isOpen,
      showModal,
      hideModal,
      setModalContent,
      showTemplate
    }),
    [isOpen, showModal, hideModal, setModalContent, showTemplate]
  )

  return (
    <ModalContext.Provider value={value}>
      {children}

      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (opts.closeOnBackdrop === false) return
          hideModal()
        }}
        title={opts.title}
        size={opts.size ?? 'lg'}
        showHeader={opts.showHeader ?? true}
        className={opts.className}
      >
        {content}
      </Modal>
    </ModalContext.Provider>
  )
}
